// Client-callable entry points for the pre-payment readiness gate.
// Thin wrappers only — all logic lives in prepayment.server.ts.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { ClaimReadiness } from "./prepayment.server";

const reviewInput = z.object({ reviewId: z.string().uuid() });

/** Readiness of a claim the caller can access. */
export const getClaimReadiness = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => reviewInput.parse(raw))
  .handler(async ({ data, context }): Promise<ClaimReadiness> => {
    const { data: review } = await context.supabase
      .from("reviews")
      .select("id")
      .eq("id", data.reviewId)
      .maybeSingle();
    if (!review) throw new Error("Claim not found");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { evaluateClaimReadiness } = await import("./prepayment.server");
    return evaluateClaimReadiness(supabaseAdmin, data.reviewId);
  });

const answersInput = z.object({
  reviewId: z.string().uuid(),
  vendor: z.string().trim().min(2).max(160).optional(),
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, "Enter a 5-digit ZIP code")
    .optional(),
  /* Recommended Service Provider Information. Customer Provided · Unverified,
     used only to improve business matching (Google Business Profile, Secretary
     of State, BBB, licensing, market pricing, legal analysis). */
  vendorPhone: z.string().trim().min(7).max(40).optional(),
  vendorAddress: z.string().trim().min(4).max(200).optional(),
  vendorCity: z.string().trim().min(2).max(80).optional(),
  vendorState: z.string().trim().min(2).max(40).optional(),
  /* Customer Provided VIN. Optional; letters/digits only, never I/O/Q. */
  vehicleVin: z
    .string()
    .trim()
    .max(17)
    .regex(/^[A-HJ-NPR-Z0-9]*$/i, "Enter a valid VIN (letters and numbers only, no I, O or Q).")
    .optional(),
});


/**
 * Persist customer-supplied context that can satisfy a readiness requirement,
 * then re-evaluate.
 *
 * Two rules are absolute:
 *   • an extracted value is never overwritten — the customer value is kept
 *     alongside it and the difference is flagged;
 *   • every stored value carries Customer Provided · Unverified provenance
 *     (source, confidence, entered_at, review_id, owner_id).
 */
export const saveReadinessAnswers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => answersInput.parse(raw))
  .handler(async ({ data, context }): Promise<ClaimReadiness> => {
    const { data: review } = await context.supabase
      .from("reviews")
      .select(
        "id,vendor,zip,vendor_phone,vendor_address,vendor_city,vendor_state,vehicle_vin,customer_id,context_provenance",
      )

      .eq("id", data.reviewId)
      .maybeSingle();
    if (!review) throw new Error("Claim not found");

    const { isUsableVendorName, normalizeVendorName } = await import("./vendor-name");
    const {
      buildProvenanceEntry,
      mergeContextProvenance,
      parseContextProvenance,
    } = await import("@/lib/context-provenance");

    // A placeholder or generic fragment is never a business identity.
    if (data.vendor !== undefined && !isUsableVendorName(data.vendor)) {
      throw new Error(
        "Enter the towing or storage business name as it appears on the invoice.",
      );
    }

    const vendorValue = normalizeVendorName(data.vendor ?? null);
    const zipValue = data.zip?.trim() || null;
    const phoneValue = data.vendorPhone?.trim() || null;
    const addressValue = data.vendorAddress?.trim() || null;
    const cityValue = data.vendorCity?.trim() || null;
    const stateValue = data.vendorState?.trim() || null;
    const { vinOrNull } = await import("@/lib/vin");
    const vinValue = vinOrNull(data.vehicleVin ?? null);
    const enteredAt = new Date().toISOString();
    const ownerId = (review as { customer_id?: string | null }).customer_id ?? null;
    const r = review as {
      vendor_phone?: string | null;
      vendor_address?: string | null;
      vendor_city?: string | null;
      vendor_state?: string | null;
      vehicle_vin?: string | null;
    };

    const entries = [
      buildProvenanceEntry({
        field: "vendor",
        value: vendorValue,
        extractedValue: review.vendor,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "zip",
        value: zipValue,
        extractedValue: review.zip,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "vendor_phone",
        value: phoneValue,
        extractedValue: r.vendor_phone ?? null,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "vendor_address",
        value: addressValue,
        extractedValue: r.vendor_address ?? null,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "vendor_city",
        value: cityValue,
        extractedValue: r.vendor_city ?? null,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "vehicle_vin",
        value: vinValue,
        extractedValue: r.vehicle_vin ?? null,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
      buildProvenanceEntry({
        field: "vendor_state",
        value: stateValue,
        extractedValue: r.vendor_state ?? null,
        reviewId: data.reviewId,
        ownerId,
        enteredAt,
      }),
    ];

    // Only fill a column the claim does not already have: extraction wins.
    const patch: {
      vendor?: string;
      zip?: string;
      vendor_phone?: string;
      vendor_address?: string;
      vendor_city?: string;
      vendor_state?: string;
      vehicle_vin?: string;
    } = {};
    if (vendorValue && !review.vendor) patch.vendor = vendorValue;
    if (zipValue && !review.zip) patch.zip = zipValue;
    if (phoneValue && !r.vendor_phone) patch.vendor_phone = phoneValue;
    if (addressValue && !r.vendor_address) patch.vendor_address = addressValue;
    if (cityValue && !r.vendor_city) patch.vendor_city = cityValue;
    if (stateValue && !r.vendor_state) patch.vendor_state = stateValue;
    if (vinValue && !r.vehicle_vin) patch.vehicle_vin = vinValue;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (entries.some(Boolean)) {
      const provenance = mergeContextProvenance(
        parseContextProvenance((review as { context_provenance?: unknown }).context_provenance),
        entries,
      );
      await supabaseAdmin
        .from("reviews")
        .update({ ...patch, context_provenance: provenance } as never)
        .eq("id", data.reviewId);
    }

    const { evaluateClaimReadiness } = await import("./prepayment.server");
    return evaluateClaimReadiness(supabaseAdmin, data.reviewId);
  });

